import type { WCardSuitePaperItemView } from '@zola/svc-web-api-ts-client';

/**
 * Formats a price for display.
 * @param priceInDollars As the name implies, the price in dollars
 * @param unitMultiplier The "bundle" size of each unit (e.g. stickers are 24 per sheet)
 * @param isStartingPrice Prepends "From" to formatted result
 * @returns A string representing the unit price of a paper product
 */
export const formatUnitCost = (
  priceInDollars?: number,
  unitMultiplier = 1,
  isStartingPrice = false
): string => {
  if (!priceInDollars || priceInDollars < 0) return '';

  const qtyPerUnit = unitMultiplier || 1;
  const prefix = isStartingPrice ? 'From ' : '';
  const suffix = qtyPerUnit > 1 ? ` for ${qtyPerUnit}` : ' each';

  return `${prefix}$${priceInDollars.toFixed(2)}${suffix}`;
};

/** Formats a price like `formatUnitCost`, but based on a paper info object */
export const formatUnitCostFromSettings = (
  paperInfo?: WCardSuitePaperItemView,
  isStartingPrice = false
) => {
  const { price_cents_min: priceCents = 0, qty_unit_multiplier: unitMultiplier = 1 } =
    paperInfo || {};

  if (!priceCents) return '';

  return formatUnitCost(priceCents / 100, unitMultiplier, isStartingPrice);
};

/** Formats a price in dollars like formatUnitCost, but adds a "+" and optional parenthesis */
export const formatExtraCost = (
  priceInDollars?: number,
  inParenthesis = false,
  unitMultiplier = 1
) => {
  const cost = formatUnitCost(priceInDollars, unitMultiplier, false);
  if (!cost) return '';
  return inParenthesis ? `(+${cost})` : `+${cost}`;
};

/** Formats a price in cents like formatUnitCost, but adds a "+" and optional parenthesis */
export const formatExtraCostCents = (
  priceInCents?: number,
  inParenthesis = false,
  unitMultiplier = 1
) => {
  if (!priceInCents) return '';
  return formatExtraCost(priceInCents / 100, inParenthesis, unitMultiplier);
};
